import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

// Component Imports
import { Dashboard } from './pages/Dashboard';
import { LoginPage } from './pages/LoginPage';
import { LocationSetup } from './pages/setup/location/LocationSetup';
import { UserManagement } from './pages/user/UserListPage';
import { PLOFormOne } from './pages/PLO/form1/PLOForm1';
import { PLOFormTwo } from './pages/PLO/form2/PLOForm2';
// import LegalAdviceFourForm from './pages/cases/Form4/LegalAdviceFourForm';
import { LegalFormSeven } from './pages/cases/Form7/LegalAdviceSevenForm';
import PLOFormThree from './pages/PLO/form3/PLOFormThree';
import PLOFormFive from './pages/PLO/form5/PLOFormFive';
import PLOFormSix from './pages/PLO/form6/PLOFormSix';
import { OtpPage } from './pages/auth/OTPPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';

// API Imports
import Enable2FA from './components/Enable2FA';
import { UserForm } from './pages/user/UserFormPage';
import { verifySession } from './services/auth.services';
// import { PLOFormOneDetailView } from './pages/PLO/form1/PLODetailView';
import type { PageKey } from './types/FormPageKey.type';
import { SettingsProvider } from './context/SettingContext';
import { LegalFormOne } from './pages/cases/Form1/LegalAdviceOneForm';
import { PLOFormOneDetailView } from './pages/PLO/form1/PLODetailView';
import { LegalFormOneDetailView } from './pages/cases/Form1/LegalOneDetailView';
import { LegalFormThree } from './pages/cases/Form3/LegalAdviceThreeForm';
import { LegalFormThreeDetailView } from './pages/cases/Form3/LegalThreeDetailView';
import { PLOFormTwoDetailView } from './pages/PLO/form2/PLOForm2DetailView';
import PLOFormFour from './pages/PLO/form4/PLOFormFour';
import { LegalFormTwo } from './pages/cases/Form2/LegalAdviceTwoForm';
import { LegalFormFive } from './pages/cases/Form5/LegalAdviceFiveForm';
import { LegalFormSix } from './pages/cases/Form6/LegalAdviceSixForm';
import { LegalFormEight } from './pages/cases/Form8/LegalAdviceEightForm';
import { LegalFormNine } from './pages/cases/form9/LegalAdviceNineForm';
import { LegalFormTen } from './pages/cases/Form10/LegalAdviceTenForm';
import { LegalFormEleven } from './pages/cases/Form11/LegalAdviceElevenForm';
import { LegalFormTwoDetailView } from './pages/cases/Form2/LegalTwoDetailView';
import { LegalFormFiveDetailView } from './pages/cases/Form5/LegalFiveDetailView';
import { LegalFormSixDetailView } from './pages/cases/Form6/LegalSixDetailView';
import { LegalFormSevenDetailView } from './pages/cases/Form7/LegalSevenDetailView';
import { LegalFormElevenDetailView } from './pages/cases/Form11/LegalElevenDetailView';
import { LegalFormTenDetailView } from './pages/cases/Form10/LegalTenDetailView';
import { LegalFormNineDetailView } from './pages/cases/form9/LegalNineDetailView';
import { LegalFormEightDetailView } from './pages/cases/Form8/LegalEightDetailView';
import { LegalFormSevenA } from './pages/cases/Form7/LegalAdviceSevenAForm';
import { LegalFormEightA } from './pages/cases/Form8/LegalAdviceEightAForm';
import { LegalFormEightADetailView } from './pages/cases/Form8/LegalEightADetialView';
import { LegalFormSevenADetailView } from './pages/cases/Form7/LegalSevenADetailView';
import LegalAdviceFourForm from './pages/cases/Form4/LegalAdviceFourForm';
// 1. Initialize TanStack Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});



export default function App() {
  const [currentPage, setCurrentPage] = useState<PageKey>(() => {
    const token = localStorage.getItem('access_token');
    return token ? 'dashboard' : 'login';
  });

  const isResetPasswordRoute = window.location.pathname === '/reset-password';
  
  const [dashTab, setDashTab] = useState<string>('dashboard');
  const [otpUserId, setOtpUserId] = useState<string>('');
  const [isLoadingSession, setIsLoadingSession] = useState<boolean>(true);

  // Check token validity against backend on initial render
  useEffect(() => {
    const checkToken = async () => {
      if (isResetPasswordRoute) {
        setIsLoadingSession(false);
        return;
      }

      const token = localStorage.getItem('access_token');
      
      if (token) {
        const isValid = await verifySession();
        if (!isValid) {
          // Token expired or revoked from another device
          handleLogout();
        }
      } else {
        setCurrentPage('login');
      }
      
      setIsLoadingSession(false);
    };

    checkToken();
  }, [isResetPasswordRoute]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    setCurrentPage('login');
  };

  // Prevent flash of dashboard while verifying token on backend
  if (isLoadingSession) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-slate-500 font-medium">Checking session...</div>
      </div>
    );
  }

  if (isResetPasswordRoute) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Routes>
      </BrowserRouter>
    );
  }

  if (currentPage === 'login') {
    return (
      <LoginPage
        onLoginSuccess={() => setCurrentPage('dashboard')}
        onRequireOtp={(userId) => {
          setOtpUserId(userId);
          setCurrentPage('otp');
        }}
      />
    );
  }

  if (currentPage === 'otp') {
    return (
      <OtpPage
        userId={otpUserId}
        onVerifySuccess={() => setCurrentPage('dashboard')}
        onCancel={() => setCurrentPage('login')}
      />
    );
  }

  const standalonePagesConfig: Record<
    Exclude<PageKey, 'login' | 'otp' | 'dashboard'>,
    {
      backLabel: string;
      systemLabel: string;
      tabTarget: string;
      component: React.ReactNode;
      useMaxWidthContainer?: boolean;
    }
  > = {
    'form-1': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-1', component: <LegalFormOne /> },
    'form-2': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-2', component: <LegalFormTwo /> },
    'form-3': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-3', component: <LegalFormThree /> },
    // 'form-4': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-4', component: <LegalAdviceFourForm /> },
    'form-5': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-5', component: <LegalFormFive /> },
    'form-6': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-6', component: <LegalFormSix /> },
    'form-7': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-7', component: <LegalFormSeven /> },
    'form-7a': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-7a', component: <LegalFormSevenA /> },
    'form-8': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-8', component: <LegalFormEight /> },
    'form-8a': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-8a', component: <LegalFormEightA /> },
    'form-9': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-9', component: <LegalFormNine /> },
    'form-10': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-10', component: <LegalFormTen /> },
    'form-11': { backLabel: '← Back to Applications', systemLabel: 'Case Registry System', tabTarget: 'form-11', component: <LegalFormEleven /> },
    'plo-form-1': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-1', component: <PLOFormOne /> },
    'plo-form-2': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-2', component: <PLOFormTwo /> },
    'plo-form-3': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-3', component: <PLOFormThree /> },
    'plo-form-4': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-4', component: <PLOFormFour /> },
    'plo-form-5': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-5', component: <PLOFormFive /> },
    'plo-form-6': { backLabel: '← Back to Applications', systemLabel: 'PLO Registry System', tabTarget: 'plo-form-6', component: <PLOFormSix /> },
    'user-form': { backLabel: '← Back to Users', systemLabel: 'User Management', tabTarget: 'users', component: <UserManagement />, useMaxWidthContainer: true },
    'location-form': { backLabel: '← Back to Dashboard', systemLabel: 'Case Registry System', tabTarget: 'locations', component: <LocationSetup /> },
    '2fa-enable': { backLabel: '← Back to Applications', systemLabel: '2FA enable', tabTarget: '2fa-enable', component: <Enable2FA /> },
  };

  if (currentPage !== 'dashboard') {
    const config = standalonePagesConfig[currentPage];

    return (
      <QueryClientProvider client={queryClient}>
        <div className="min-h-screen bg-slate-50">
          <nav className="border-b border-slate-200 bg-white px-6 py-4">
            <div className="mx-auto max-w-4xl flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setDashTab(config.tabTarget);
                  setCurrentPage('dashboard');
                }}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline"
              >
                {config.backLabel}
              </button>
              <span className="text-sm font-semibold text-slate-500">{config.systemLabel}</span>
            </div>
          </nav>

          <div className={config.useMaxWidthContainer ? 'py-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : 'py-6'}>
            {config.component}
          </div>
        </div>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <Dashboard
                  onLogout={handleLogout}
                  activeTabId={dashTab}
                  onTabChange={(id) => setDashTab(id)}
                />
              }
            />
            <Route path="/user-form/create" element={<UserForm />} />
            <Route path="/user-form/edit/:id" element={<UserForm />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route path="/plo-form-one/create" element={<PLOFormOne />} />
            <Route path="/plo-form-one/edit/:id" element={<PLOFormOne />} />
            <Route path="/plo-form-one-detail/:id" element={<PLOFormOneDetailView />} />

            <Route path="/plo-form-two/create" element={<PLOFormTwo />} />
            <Route path="/plo-form-two/edit/:id" element={<PLOFormTwo />} />
            <Route path="/plo-form-two-detail/:id" element={<PLOFormTwoDetailView />} />

            <Route path="/plo-form-three/create" element={<PLOFormThree />} />
            <Route path="/plo-form-three/edit/:id" element={<PLOFormThree />} />

            <Route path="/plo-form-four/create" element={<PLOFormFour />} />
            <Route path="/plo-form-four/edit/:id" element={<PLOFormFour />} />

            <Route path="/plo-form-five/create" element={<PLOFormFive />} />
            <Route path="/plo-form-five/edit/:id" element={<PLOFormFive />} />

            <Route path="/plo-form-Six/create" element={<PLOFormSix />} />
            <Route path="/plo-form-Six/edit/:id" element={<PLOFormSix />} />

            <Route path="/legal-form-one/create" element={<LegalFormOne />} />
            <Route path="/legal-form-one/edit/:id" element={<LegalFormOne />} />
            <Route path="/legal-form-one-detail/:id" element={<LegalFormOneDetailView />} />

            <Route path="/legal-form-two/create" element={<LegalFormTwo />} />
            <Route path="/legal-form-two/edit/:id" element={<LegalFormTwo />} />
            <Route path="/legal-form-two-detail/:id" element={<LegalFormTwoDetailView />} />

            <Route path="/legal-form-four/create" element={<LegalAdviceFourForm />} />
            <Route path="/legal-form-four/edit/:id" element={<LegalAdviceFourForm />} />
            {/* <Route path="/legal-form-two-detail/:id" element={<LegalFormTwoDetailView />} /> */}

            <Route path="/legal-form-three/create" element={<LegalFormThree />} />
            <Route path="/legal-form-three/edit/:id" element={<LegalFormThree />} />
            <Route path="/legal-form-three-detail/:id" element={<LegalFormThreeDetailView />} />
            {/* <Route path="/legal-form-four/:id" element={<LegalAdviceFourForm />} /> */}

            <Route path="/legal-form-five/create" element={<LegalFormFive />} />
            <Route path="/legal-form-five/edit/:id" element={<LegalFormFive />} />
            <Route path="/legal-form-five-detail/:id" element={<LegalFormFiveDetailView />} />

            <Route path="/legal-form-six/create" element={<LegalFormSix />} />
            <Route path="/legal-form-six/edit/:id" element={<LegalFormSix />} />
            <Route path="/legal-form-six-detail/:id" element={<LegalFormSixDetailView />} />

            <Route path="/legal-form-seven/create" element={<LegalFormSeven />} />
            <Route path="/legal-form-seven/edit/:id" element={<LegalFormSeven />} />
            <Route path="/legal-form-seven-detail/:id" element={<LegalFormSevenDetailView />} />

            <Route path="/legal-form-seven-a/create" element={<LegalFormSevenA />} />
            <Route path="/legal-form-seven-a/edit/:id" element={<LegalFormSevenA />} />
            <Route path="/legal-form-seven-a-detail/:id" element={<LegalFormSevenADetailView />} />

            <Route path="/legal-form-eight/create" element={<LegalFormEight />} />
            <Route path="/legal-form-eight/edit/:id" element={<LegalFormEight />} />
            <Route path="/legal-form-eight-detail/:id" element={<LegalFormEightDetailView />} />

            <Route path="/legal-form-eight-a/create" element={<LegalFormEightA />} />
            <Route path="/legal-form-eight-a/edit/:id" element={<LegalFormEightA />} />
            <Route path="/legal-form-eight-a-detail/:id" element={<LegalFormEightADetailView />} />


            <Route path="/legal-form-nine/create" element={<LegalFormNine />} />
            <Route path="/legal-form-nine/edit/:id" element={<LegalFormNine />} />
            <Route path="/legal-form-nine-detail/:id" element={<LegalFormNineDetailView />} />

            <Route path="/legal-form-ten/create" element={<LegalFormTen />} />
            <Route path="/legal-form-ten/edit/:id" element={<LegalFormTen />} />
            <Route path="/legal-form-ten-detail/:id" element={<LegalFormTenDetailView />} />

            <Route path="/legal-form-eleven/create" element={<LegalFormEleven />} />
            <Route path="/legal-form-eleven/edit/:id" element={<LegalFormEleven />} />
            <Route path="/legal-form-eleven-detail/:id" element={<LegalFormElevenDetailView />} />


            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </SettingsProvider>
    </QueryClientProvider>
  );
}